import { useState } from 'react'
import { apiService } from '../../services/apiService'
import { Conversation } from '../../types/conversation'
import BPMNLiveViewer from '../BPMNViewer/BPMNLiveViewer'

interface BlockTesterProps {
  onConversationCreate: (conversation: Conversation) => void
  onConversationUpdate: (conversation: Conversation) => void
}

// Block definitions - same as test-blocks.sh
const AVAILABLE_BLOCKS = [
  { id: 'FETCHBALANCE', name: 'Fetch Balance', prompt: 'Create a workflow that fetches the balance for a user' },
  { id: 'PREPAREANDSENDNOTIFICATION', name: 'Send Email', prompt: 'Create a workflow that sends an email notification to a user' },
  { id: 'UPDATEUSERKYC', name: 'Update User KYC', prompt: 'Create a workflow that updates user profile information' },
  { id: 'MONITORCOUNTER', name: 'Monitor Counter', prompt: 'Create a workflow that monitors a counter for a user' },
  { id: 'GETCOUNTER', name: 'Get Counter', prompt: 'Create a workflow that gets the current counter value for a user' },
  { id: 'STOREVALUE', name: 'Store Value', prompt: 'Create a workflow that stores a value globally' },
  { id: 'RETRIEVEVALUES', name: 'Retrieve Values', prompt: 'Create a workflow that retrieves a stored value' },
  { id: 'PROCESSPAYMENT', name: 'Process Payment', prompt: 'Create a workflow that processes a payment from one user to another' },
  { id: 'APPLYCHARGE', name: 'Apply Charge', prompt: 'Create a workflow that applies a fee to a transaction' },
  { id: 'ONBOARDUSER', name: 'Onboard User', prompt: 'Create a workflow that onboards a new user' },
]

export default function BlockTester({ onConversationCreate, onConversationUpdate }: BlockTesterProps) {
  const [selectedBlock, setSelectedBlock] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [status, setStatus] = useState<string>('')
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null)
  const [generatedBpmn, setGeneratedBpmn] = useState<string>('')

  const selectedBlockData = AVAILABLE_BLOCKS.find(block => block.id === selectedBlock)

  const handleTestBlock = async () => {
    if (!selectedBlockData) return

    setIsLoading(true)
    setStatus('Creating conversation...')

    try {
      // Create conversation in non-conversational mode (like shell script)
      const conversation = await apiService.createConversation(false)
      setCurrentConversation(conversation)
      onConversationCreate(conversation)
      setStatus('Conversation created successfully')

      // Send message
      setStatus('Sending prompt...')
      const response = await apiService.sendMessage(conversation.id, selectedBlockData.prompt)
      
      // Check if we got a valid response with BPMN data
      if (response && response.bpmn && response.readiness > 0) {
        setStatus('✅ Block tested successfully!')
        setGeneratedBpmn(response.bpmn)
        onConversationUpdate(response)
      } else if (response && response.response) {
        // Show the response message if no BPMN generated
        setStatus(`⚠️ ${response.response}`)
        setGeneratedBpmn('')
        onConversationUpdate(response)
      } else {
        setStatus(`❌ Test failed: No valid response received`)
        setGeneratedBpmn('')
      }
    } catch (error) {
      console.error('Block test failed:', error)
      setStatus(`❌ Test failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleExportStandard = () => {
    if (!generatedBpmn || !selectedBlock) return
    
    const blob = new Blob([generatedBpmn], { type: 'application/xml' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${selectedBlock}_standard_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}.bpmn`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    setStatus('✅ Standard BPMN exported successfully!')
  }

  const handleExportMobiFin = async () => {
    if (!currentConversation) return

    try {
      setStatus('Exporting MobiFin BPMN...')
      const API_BASE = import.meta.env.VITE_API_BASE_URL || ''
      const response = await fetch(`${API_BASE}/api/v1/conversations/${currentConversation.id}/bpmn?compatibility=mobifin&format=xml`)
      
      if (!response.ok) {
        throw new Error('Failed to get MobiFin BPMN')
      }
      
      const bpmnXml = await response.text()
      if (bpmnXml) {
        const blob = new Blob([bpmnXml], { type: 'application/xml' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${selectedBlock}_mobifin_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}.bpmn`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        setStatus('✅ MobiFin BPMN exported successfully!')
      } else {
        setStatus('❌ MobiFin export failed')
      }
    } catch (error) {
      console.error('MobiFin export failed:', error)
      setStatus(`❌ MobiFin export failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  return (
    <div className="h-full overflow-y-auto bg-white">
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Block Testing</h1>
          <p className="text-gray-600">
            Test individual MobiFin blocks and export BPMN files for platform testing.
          </p>
        </div>

        {/* Block Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Block to Test
          </label>
          <select
            value={selectedBlock}
            onChange={(e) => setSelectedBlock(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0066CC] focus:border-[#0066CC]"
          >
            <option value="">Choose a block...</option>
            <optgroup label="Priority 1 (Simple)">
              {AVAILABLE_BLOCKS.slice(0, 7).map(block => (
                <option key={block.id} value={block.id}>
                  {block.name} ({block.id})
                </option>
              ))}
            </optgroup>
            <optgroup label="Priority 2 (Complex)">
              {AVAILABLE_BLOCKS.slice(7).map(block => (
                <option key={block.id} value={block.id}>
                  {block.name} ({block.id})
                </option>
              ))}
            </optgroup>
          </select>
        </div>

        {/* Generated Prompt */}
        {selectedBlockData && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Generated Prompt
            </label>
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
              <p className="text-sm text-gray-800">{selectedBlockData.prompt}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mb-6">
          <button
            onClick={handleTestBlock}
            disabled={!selectedBlock || isLoading}
            className={`px-6 py-3 rounded-md font-medium transition-colors ${
              !selectedBlock || isLoading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-[#0066CC] text-white hover:bg-[#004499]'
            }`}
          >
            {isLoading ? 'Testing...' : 'Test Block'}
          </button>
        </div>

        {/* BPMN Preview and Export Section */}
        {generatedBpmn && (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Generated BPMN</h3>
            
            {/* BPMN Preview */}
            <div className="mb-4">
              <div className="bg-gray-50 border border-gray-200 rounded-md h-96">
                {currentConversation && (
                  <BPMNLiveViewer
                    conversationId={currentConversation.id}
                    initialBpmn={generatedBpmn}
                    showControls={false}
                  />
                )}
              </div>
            </div>

            {/* Export Buttons */}
            <div className="flex space-x-4">
              <button
                onClick={handleExportStandard}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Export Standard BPMN
              </button>
              
              <button
                onClick={handleExportMobiFin}
                disabled={!currentConversation}
                className={`px-4 py-2 rounded-md transition-colors ${
                  !currentConversation
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[#FF6600] text-white hover:bg-[#CC5200]'
                }`}
              >
                Export MobiFin BPMN
              </button>
            </div>
          </div>
        )}

        {/* Status */}
        {status && (
          <div className="mb-6">
            <div className={`p-3 rounded-md ${
              status.includes('✅') 
                ? 'bg-green-50 text-green-800 border border-green-200'
                : status.includes('❌')
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-blue-50 text-blue-800 border border-blue-200'
            }`}>
              <p className="text-sm">{status}</p>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Testing Instructions</h3>
          <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
            <li>Select a block from the dropdown above</li>
            <li>Review the generated prompt</li>
            <li>Click "Test Block" to create and test the workflow</li>
            <li>Review the generated BPMN in the preview area</li>
            <li>Export either Standard BPMN or MobiFin format</li>
            <li>Import the MobiFin BPMN file into the MobiFin platform for validation</li>
          </ol>
        </div>
      </div>
    </div>
  )
}